// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from './component';
import { SmallButton } from './small-button';
import { Tooltip } from './tooltip';
import { Cookie } from './cookie';
import { SmallTools } from './small-tools';
import { messageBox } from './message-box';
import { ListContextMenu } from './list-context-menu';
import { DropdownManager } from './dropdown-manager';
import {
    escapeAttribute,
    render,
    prepareUrl,
    addUrlParams,
    getComponent,
    createComponent,
    createElement,
    showInternalError,
    addStatusMessage,
    clearStatusMessages,
} from './common';
import { redirect } from './form-redirect';

/**
 * @class Jsw.list
 * @singleton
 */
export const list = {
    /**
     * @property {Object} COLUMN_SELECTION
     */
    COLUMN_SELECTION: {
        cls: 'select',
        headerCls: 'select',
        hideDisabled: false,
        headerRenderer(column) {
            return '<input type="checkbox" class="checkbox" name="listGlobalCheckbox"/>';
        },
        renderer(item, isDisabled) {
            if (!isDisabled) {
                return `<input type="checkbox" class="checkbox" name="listCheckbox[]" value="${escapeAttribute(item.id)}"/>`;
            }

            const html =
                '<div>' +
                    '<span class="checkbox-disabled-wrapper"></span>' +
                    '<input style="z-index: 1;" disabled="disabled" type="checkbox" class="checkbox"/>' +
                '</div>';

            return html;
        },
    },

    /**
     * @property {Object} COLUMN_ACTIONS
     */
    COLUMN_ACTIONS: {
        header: '',
        cls: 'min',
        sortable: false,
        renderer(item) {
            if (!item.actions || !item.actions.length) {
                return '';
            }
            const rowId = item.id ? escapeAttribute(item.id) : '';
            return '<span class="btn-group list-menu">' +
                `<span class="btn btn-list-menu dropdown-toggle" data-row-id="${rowId}">` +
                    '<button type="button">' +
                        '<i class="icon">' +
                            `<img src="${Jsw.skinUrl}/icons/16/plesk/menu.png" alt="">` +
                        '</i> ' +
                        '<em class="caret"></em>' +
                    '</button>' +
                '</span>' +
            '</span>';
        },
    },

    /**
     * @property {Object} ITEMS_UNLIMITED
     */
    ITEMS_UNLIMITED: 100000,
};

/**
 * @class Jsw.list.AdditionalActions
 * @extends Jsw.Component
 */
list.AdditionalActions = Class.create(Component, {
    _tag: 'div',

    _initConfiguration($super, config) {
        $super(config);

        this._operations = this._getConfigParam('operations', null);
        this._cls = this._getConfigParam('cls', 'actions-menu');
        this._titleAddCls = this._getConfigParam('titleAddCls', 'sb-more-actions');
    },

    _initComponentElement($super) {
        $super();

        this._componentElement.insert({
            bottom:
                '<div class="popup-box collapsed">' +
                    '<table class="popup-wrapper" cellspacing="0"><tbody><tr>' +
                        '<td class="popup-container">' +
                            '<div class="c1"><div class="c2"><div class="c3"><div class="c4"><div class="c5">' +
                                '<div class="popup-heading">' +
                                    '<div class="heading-area">' +
                                        '<span class="close"></span>' +
                                        '<h4>' +
                                            `<span>${this._getConfigParam('title', '')}</span>` +
                                        '</h4>' +
                                    '</div>' +
                                '</div>' +
                                '<div class="popup-content">' +
                                    '<div class="popup-content-area">' +
                                        `<ul id="${this._id}-operations">` +
                                        '</ul>' +
                                    '</div>' +
                                '</div>' +
                            '</div></div></div></div></div>' +
                        '</td>' +
                    '</tr></tbody></table>' +
                '</div>',
        });
    },

    _addEvents($super) {
        $super();

        const context = this;
        $(document.body).observe('click', function () {
            context.collapse();
        });
    },

    enable() {
        this._titleButton.enable();
    },

    disable() {
        this._titleButton.disable();
    },

    toggle() {
        const popup = this._componentElement.down('.popup-box');

        if (popup) {
            popup.toggleClassName('collapsed');
        }

        const button = this._componentElement.down(`.${this._titleAddCls}`);

        if (button) {
            button.toggleClassName('active');
        }
    },

    collapse() {
        const popup = this._componentElement.down('.popup-box');

        if (popup) {
            popup.addClassName('collapsed');
        }

        const button = this._componentElement.down(`.${this._titleAddCls}`);

        if (button) {
            button.removeClassName('active');
        }
    },

    render($super) {
        $super();

        this._titleButton = new SmallButton({
            title: this._getConfigParam('title', ''),
            cls: 's-btn',
            addCls: this._titleAddCls,
            handler: (function (event) {
                Event.stop(event);
                this.toggle();
            }).bind(this),
        });
        render(this._componentElement, this._titleButton);

        if (!this._operations) {
            return;
        }

        const operationsArea = this._componentElement.down(`#${this._id}-operations`);
        this._operations.each(function (config) {
            const liElement = new Element('li');
            operationsArea.insert(liElement);

            const operation = createComponent(config);
            const wrapperClass = operation.getWrapperClass();
            if (wrapperClass) {
                liElement.addClassName(wrapperClass);
            }

            render(liElement, operation);
        }, this);

        if (this._getConfigParam('disabled', false)) {
            this.disable();
        } else {
            this.enable();
        }
    },
});

/**
 * @class Jsw.List
 * The class for the implementation of the widget "List". In addition to displaying the data,
 * it is possible to change the sorting, search, perform group operations.
 * @extends Jsw.Component
  */
export const List = Class.create(Component, {
    /**
     * @cfg {String} listCls=list
     */
    /**
     * @cfg {String} dataUrl=""
     */
    /**
     * @cfg {Object[]} operations List operations.
     * See {@link Jsw.SmallTools}.{@link Jsw.SmallTools#operations operations} for details.
     */
    /**
     * @cfg {Object[]} columns
     */
    /**
     * @cfg {Object[]} itemActions
     */

    _initConfiguration($super, config) {
        $super(config);

        this._cls += ' js-list';
        this._listCls = this._getConfigParam('listCls', 'list');
        this._tableId = `${this._id}-table`;
        this._dataUrl = this._getConfigParam('dataUrl', '');
        this._operations = this._getConfigParam('operations', null);
        this._itemActions = this._getConfigParam('itemActions', {});
        this._columns = this._getConfigParam('columns', []);
        this._limitValues = this._getConfigParam('limitValues', [10, 25, 100, list.ITEMS_UNLIMITED]);
        this._splitListData(this._getConfigParam('data', { data: null, pathbar: null, state: null, pager: null, locale: null, additional: null }));
        this._searchFilters = $H(this._getConfigParam('searchFilters', {}));
        this._searchOveral = this._getConfigParam('searchOveral', null);
        this._pageable = this._getConfigParam('pageable', true);
        this._disabledSelectHint = this._getConfigParam('disabledSelectHint', '');
        this._clearStatusOnReload = this._getConfigParam('clearStatusOnReload', true);
        this.isDisabledItem = this._getConfigParam('isDisabledItem', function () {
            return false;
        });
        this._onRedraw = this._getConfigParam('onRedraw', function () {});
        this._getTotalInfo = this._getConfigParam('getTotalInfo', function () {
            return this.lmsg('recordsTotal', { total: this._pager.totalItemCount });
        });
        this._getRowClass = this._getConfigParam('getRowClass', function () {
            return '';
        });

        if (Object.isArray(this._operations)) {
            this._operations = this._operations.compact();
            if (!this._operations.length) {
                this._operations = null;
            }
        }
    },

    _getSearchOveralField() {
        if (null === this._searchOveral) {
            this._searchFilters.each(function (pair) {
                if (pair.value.overal) {
                    this._searchOveral = pair.key;
                }
            }, this);
            if (null === this._searchOveral && 1 === this._searchFilters.size()) {
                this._searchOveral = this._searchFilters.keys().first();
            }
            if (null === this._searchOveral) {
                this._searchOveral = false;
            }
        }

        return this._searchOveral;
    },

    _initComponentElement($super) {
        $super();

        this._updateComponentElement(`<div id="${this._id}-container"></div>`);
        this._initDisablerOverlay();
    },

    _splitListData(listData, place) {
        if (!listData || !listData.data) {
            return false;
        }

        const dataLen = listData.data.length;
        if ('before' === place) {
            this._data = listData.data.concat(this._data);
        } else if ('after' === place) {
            this._data = this._data.concat(listData.data);
        } else {
            this._data = listData.data;
        }
        this._state = listData.state || {};
        this._pager = listData.pager || {};
        if (listData.locale) {
            Object.extend(this._locale.messages, listData.locale);
        }
        if (listData.pathbar) {
            this._pathbar = listData.pathbar;
        }
        if (listData.additional) {
            this._additionalData = this._additionalData || {};
            this._additionalData = Object.extend(this._additionalData, listData.additional);
        }
        return dataLen;
    },

    _isColumnSelectionPresent() {
        return this._getColumns().any(function (column) {
            return column === list.COLUMN_SELECTION;
        });
    },

    /**
     * @param {String} dataIndex
     */
    onHeaderClick(dataIndex) {
        this._state.sortField = dataIndex;
        this._state.sortDirection = ('down' === this._state.sortDirection)
            ? 'up'
            : 'down';

        this.reload(`/sort-field/${dataIndex}/sort-dir/${this._state.sortDirection}`);
    },

    /**
     * @param {Boolean} [force]
     */
    updateFilter(force) {
        if (!$(this._tableId)) {
            this.checkEmptyList();
            return;
        }
        let filterChanged = false;
        let hasFilter = false;
        this._getColumns().filter(function (column) {
            return 'function' !== typeof column.isVisible || column.isVisible();
        }).forEach(function (column) {
            if (!column.filter || !column.filter.type) {
                return;
            }

            let input;
            if (column.filter.options) {
                if (column.filter.multi) {
                    input = this._componentElement.down(`th[data-index="${column.dataIndex}"]`).select('input[type=checkbox]:checked');
                } else {
                    input = this._componentElement.down(`th[data-index="${column.dataIndex}"] select`);
                }
            } else {
                input = this._componentElement.down(`th[data-index="${column.dataIndex}"] input`);
            }
            if (!input) {
                column.filter.value = '';
                return;
            }

            hasFilter = true;

            let value;
            if (column.filter.multi) {
                value = [];
                input.each(function (el) {
                    value.push(el.value.toLowerCase());
                });
                if (value.length === column.filter.options.length) {
                    value = [];
                }
            } else {
                value = input.value.toLowerCase();
            }
            if (value === column.filter.value) {
                return;
            }

            column.filter.value = value;
            filterChanged = true;
        }, this);

        if (!hasFilter || (!force && !filterChanged)) {
            this.checkEmptyList();
            return;
        }
        let oddEvenRowClass = 'even';
        $(this._tableId).select('tr.odd, tr.even').each(function (row, rowIndex) {
            const cells = row.childElements();
            let match = true;

            this._getColumns().filter(function (column) {
                return 'function' !== typeof column.isVisible || column.isVisible();
            }).forEach(function (column, columnIndex) {
                const value = this._data[rowIndex][column.dataIndex];
                if (cells[columnIndex]._filtered) {
                    if (column.noEscape) {
                        cells[columnIndex].innerHTML = value;
                    } else {
                        cells[columnIndex].innerHTML = value.escapeHTML();
                    }
                    delete cells[columnIndex]._filtered;
                }

                if (!match || !column.filter || !column.filter.type || 0 === column.filter.value.length) {
                    return;
                }

                if (column.filter.type === 'date') {
                    if (new Date(value.replace(/ /g, 'T')) < new Date(column.filter.value.replace(/ /g, 'T'))) {
                        match = false;
                    }
                    return;
                }

                if (column.filter.options) {
                    if (column.filter.multi) {
                        if (column.filter.value.indexOf(value.toLowerCase()) === -1) {
                            match = false;
                        }
                    } else if (value.toLowerCase() !== column.filter.value) {
                        match = false;
                    }
                    return;
                }

                const pos = value.toLowerCase().indexOf(column.filter.value);

                if (pos === -1 || (column.filter.startsWith && pos !== 0)) {
                    match = false;
                    return;
                }

                if (column.noEscape) {
                    cells[columnIndex].innerHTML = `${value.substr(0, pos)
                    }<b class="search-result-label">${
                        value.substr(pos, column.filter.value.length)
                    }</b>${
                        value.substr(pos + column.filter.value.length)}`;
                } else {
                    cells[columnIndex].innerHTML = `${value.substr(0, pos).escapeHTML()
                    }<b class="search-result-label">${
                        value.substr(pos, column.filter.value.length).escapeHTML()
                    }</b>${
                        value.substr(pos + column.filter.value.length).escapeHTML()}`;
                }
                cells[columnIndex]._filtered = true;
            }, this);

            if (match) {
                row.removeClassName(oddEvenRowClass);
                oddEvenRowClass = oddEvenRowClass === 'odd' ? 'even' : 'odd';
                row.addClassName(oddEvenRowClass);
                row.show();
            } else {
                row.hide();
            }
        }, this);
        this.checkEmptyList();
    },

    /**
     * @param {String} value
     */
    onLimitClick(value) {
        this.reload({ 'items-per-page': value });
    },

    /**
     * @param {String} page
     */
    onPagerClick(page) {
        this.reload({ page });
    },

    onSelectButtonClick(event) {
        const globalCheckbox = this._componentElement.select('input[name="listGlobalCheckbox"]').first();
        globalCheckbox.checked = !globalCheckbox.checked;
        this.onListGlobalCheckboxClick(event);
        event.stopPropagation();
    },

    onListGlobalCheckboxClick(event) {
        const actionsBox = this._componentElement.select('.actions-box').first();
        const globalCheckbox = this._componentElement.select('input[name="listGlobalCheckbox"]').first();

        if (actionsBox) {
            const selectButton = actionsBox.select('.r-actions-select').first();
            if (globalCheckbox.checked) {
                selectButton.addClassName('r-actions-item-active');
            } else {
                selectButton.removeClassName('r-actions-item-active');
            }
        }

        this._componentElement.select('input[name="listCheckbox[]"]').each(function (checkbox) {
            if (globalCheckbox.checked) {
                checkbox.up('tr').addClassName('selected');
            } else {
                checkbox.up('tr').removeClassName('selected');
            }
            checkbox.checked = globalCheckbox.checked;
        });
    },

    onPagerButtonClick(event) {
        const paging = this._componentElement.select('.paging').first();
        const show = !paging.hasClassName('r-paging-visible');
        if (this._actionBox) {
            this._actionBox._hideResponsiveButtonBlocks();
        }
        if (show) {
            paging.addClassName('r-paging-visible');
        }
        event.stopPropagation();
    },

    listHeadersView() {
        const headers = [];
        const filters = [];
        let hasFilter = false;
        let colspan = 0;

        this._getColumns().forEach(function (column) {
            if (colspan) {
                colspan--;
                return;
            }

            if ('function' === typeof column.isVisible) {
                if (!column.isVisible()) {
                    return;
                }
            }

            if (column.headerColspan) {
                colspan = column.headerColspan;
            }

            let { header } = column;

            let headerCls = column.headerCls ? `.${column.headerCls}` : '';

            if ('undefined' !== typeof column.headerRenderer) {
                header = column.headerRenderer(column);
            } else {
                let sortCssClass = '';
                if (this._state.sortField === column.dataIndex) {
                    sortCssClass = 'down' === this._state.sortDirection ? '' : '.sort-up';
                    headerCls += '.sort';
                }

                if (column.sortable) {
                    header = createElement(`a${sortCssClass}`, { onclick: this.onHeaderClick.bind(this, column.dataIndex) }, header);
                }
            }
            headers.push(createElement(`th${headerCls}${colspan ? `[colspan="${colspan}"]` : ''}`, column.headerParams, header));

            let filter = '';
            if (column.filter) {
                hasFilter = true;
                filter = List.Filters.create(Object.extend({
                    name: column.dataIndex,
                    locale: this.getLocale(),
                }, column.filter), this.updateFilter.bind(this, false));
            }
            filters.push(createElement('th', { 'data-index': column.dataIndex }, filter));
        }, this);

        return createElement('thead',
            hasFilter ? createElement('tr.list-search-filter', filters) : '',
            createElement('tr', headers)
        );
    },

    _getListDataHtml(listData, indexOffset) {
        if ('undefined' === typeof listData) {
            listData = this._data;
        }

        if (!listData) {
            return;
        }

        indexOffset = indexOffset || 0;

        return Object.keys(listData).map(function (index) {
            return this._getRowHtml(listData[index], index, indexOffset);
        }.bind(this)).join('');
    },

    // One data row as html
    _getRowHtml(item, index, indexOffset) {
        let html = '';
        const rowClass = this._getRowClass(item);
        const oddEvenRowClass = (index + indexOffset) % 2 ? 'even' : 'odd';
        const rowId = item.id ? escapeAttribute(item.id) : '';
        html += `<tr class="${rowClass} ${oddEvenRowClass}" data-row-id="${rowId}">`;

        this._getColumns().filter(function (column) {
            return 'function' !== typeof column.isVisible || column.isVisible();
        }).forEach(function (column) {
            let data = '';

            if (Object.isFunction(column.renderer)) {
                const isDisabled = this.isDisabledItem(item);

                if (isDisabled && ('undefined' !== typeof column.hideDisabled) && column.hideDisabled) {
                    data = '';
                } else {
                    data = column.renderer.call(this, item, isDisabled);
                }
            } else if ('undefined' !== typeof column.dataIndex && 'undefined' !== typeof item[column.dataIndex]) {
                if (column.noEscape || !item[column.dataIndex].escapeHTML) {
                    data = item[column.dataIndex];
                } else {
                    data = item[column.dataIndex].escapeHTML();
                }
            }

            let itemCls = '';

            if ('undefined' !== typeof column.cls) {
                itemCls = column.cls;
            }

            html += `<td class="${itemCls}">${data}</td>`;
        }, this);

        html += '</tr>';
        return html;
    },

    listView() {
        return createElement(`.${this._listCls}`,
            createElement(`table#${this._tableId}[width="100%"][cellspacing="0"]`,
                this.listHeadersView(),
                this._getListDataHtml()
            )
        );
    },

    containerView() {
        return createElement(`#${this._id}-container`,
            this._getPathbarHtml(),
            this._getPagerHtml(),
            this.listView(),
            this._getPagerHtml(),
            this._getEmptyHtml()
        );
    },

    checkEmptyList() {
        const isEmpty = this.isListEmpty();
        this._componentElement.select('.js-empty-list').invoke('toggle', isEmpty);
        this._componentElement.select('.paging').invoke('toggle', !isEmpty);
        const listSelector = this._listCls.split(' ').reduce(function (previousValue, currentValue) {
            if (currentValue !== '') {
                previousValue += `.${currentValue}`;
            }
            return previousValue;
        }, '');
        this._componentElement.select(listSelector).invoke('toggle', !isEmpty);
    },

    isListEmpty() {
        if (!$(this._tableId)) {
            return !this._data.length;
        }
        return !$(this._tableId).select('tr.odd,tr.even').filter(Element.visible).length;
    },

    _getPathbarHtml() {
        if (!this._pathbar) {
            return '';
        }

        return `<div id="${this._id}-pathbar"></div>`;
    },

    _getPagerHtml() {
        if (!this._pageable) {
            return '';
        }
        const totalRecords = this._getTotalInfo();

        let html =
            '<div class="paging">' +
                '<div class="paging-area">' +
                    `<span class="paging-info"  onclick="Jsw.getComponent('${this._id}').onPagerButtonClick(event);">${totalRecords}</span>` +
                    `<span class="paging-view">${this.lmsg('numberOfItemPerPage')}: ${this._getLimitValuesHtml()}</span>`;

        if (this._pager.pageCount > 1) {
            html += `<span class="paging-nav">${this.lmsg('pages')}: ${this._getPagesItemsHtml()}</span>`;
        }

        html += '</div></div>';

        return html;
    },

    _getLimitValuesHtml() {
        let html = '';

        this._limitValues.each(function (limitValue) {
            const limitValueTitle = (list.ITEMS_UNLIMITED === limitValue)
                ? this.lmsg('allItems')
                : limitValue;

            if (this._pager.itemCountPerPage !== limitValue) {
                html += `<a href="#" onclick="Jsw.getComponent('${this._id}').onLimitClick(${limitValue});">${limitValueTitle}</a> `;
            } else {
                html += `<span>${limitValueTitle}</span> `;
            }
        }, this);

        return html;
    },

    _getPagesItemsHtml() {
        let html = '';

        if (this._pager.first !== this._pager.current) {
            html += `<a href="#" onclick="${this._getPageItemLink(this._pager.first)}">${this.lmsg('firstPage')}</a> `;
        } else {
            html += `<span>${this.lmsg('firstPage')}</span> `;
        }

        if (this._pager.previous) {
            html += `<a href="#" onclick="${this._getPageItemLink(this._pager.previous)}">&lt;&lt;</a> `;
        } else {
            html += '<span>&lt;&lt;</span> ';
        }

        $H(this._pager.pagesInRange).values().each(function (page) {
            if (page !== this._pager.current) {
                html += `<a href="#" onclick="${this._getPageItemLink(page)}">${page}</a> `;
            } else {
                html += `${page} `;
            }
        }, this);

        if (this._pager.next) {
            html += `<a href="#" onclick="${this._getPageItemLink(this._pager.next)}">&gt;&gt;</a> `;
        } else {
            html += '<span>&gt;&gt;</span> ';
        }

        if (this._pager.last !== this._pager.current) {
            html += `<a href="#" onclick="${this._getPageItemLink(this._pager.last)}">${this.lmsg('lastPage')}</a> `;
        } else {
            html += `<span>${this.lmsg('lastPage')}</span> `;
        }

        return html;
    },

    _getPageItemLink(page) {
        return `Jsw.getComponent('${this._id}').onPagerClick(${page});`;
    },

    _getEmptyHtml() {
        return `<p class="js-empty-list empty-list text-muted">${this.lmsg('noEntriesFound')}</p>`;
    },

    _onSearchClick(form) {
        this.reload(`?${form.serialize()}`);
    },

    _onResetSearchClick() {
        this.reload('/reset-search/true/');
    },

    _addSelectionHandlers() {
        const context = this;
        let selectButton;

        const actionsBox = this._componentElement.select('.actions-box').first();
        if (actionsBox) {
            selectButton = new Element('li', { class: 'r-actions-item r-actions-select' })
                .update('<span></span>')
                .observe('click', this.onSelectButtonClick.bindAsEventListener(this));
            this._componentElement.down('.actions-box .r-actions-list').insert({ top: selectButton });
        }

        const globalCheckbox = this._componentElement.select('input[name="listGlobalCheckbox"]').first();
        globalCheckbox.observe('click', this.onListGlobalCheckboxClick.bind(this));

        this._componentElement.select('input[name="listCheckbox[]"]').each(function (checkbox) {
            checkbox.observe('click', function () {
                if (checkbox.checked) {
                    checkbox.up('tr').addClassName('selected');
                } else {
                    checkbox.up('tr').removeClassName('selected');
                    globalCheckbox.checked = false;
                    if (selectButton) {
                        selectButton.removeClassName('r-actions-item-active');
                    }
                }
            });
        });

        $(document).observe('click', function (event) {
            if (event.findElement('.objects-toolbar')) {
                return;
            }

            context._hideItemsNotSelectedWarning();
        });
        $(document).observe('touchstart', function () {
            context._hideItemsNotSelectedWarning();
        });
    },

    _addPathbar() {
        if (!this._pathbar) {
            return;
        }

        render($(`${this._id}-pathbar`), createComponent(this._pathbar));
    },

    disable() {
        this._disablerOverlay.show();
        this._disablerOverlay.clonePosition(this._componentElement);
    },

    enable() {
        this._disablerOverlay.hide();
    },

    getSelectedItemsIds() {
        return this._componentElement.select('input[name="listCheckbox[]"]').filter(function (checkbox) {
            return checkbox.checked;
        }).map(function (checkbox) {
            return checkbox.value;
        });
    },

    getSelectedItems() {
        const ids = [];
        const selectedItems = [];

        this.getSelectedItemsIds().each(function (id) {
            ids.push(id);
        });

        this._data.each(function (dataItem) {
            const itemIndex = ids.indexOf(dataItem.id);
            if (itemIndex < 0) {
                return;
            }

            selectedItems.push(dataItem);
        });

        return selectedItems;
    },

    checkNonEmptySelection() {
        if (!this.getSelectedItemsIds().length) {
            this._showItemsNotSelectedWarning();
            return false;
        }
        this._hideItemsNotSelectedWarning();
        return true;
    },

    execLongGroupOperation(params, event) {
        let itemId;
        params.onSuccess = function () {
            const progressBar = getComponent('asyncProgressBarWrapper');
            progressBar.removePreparingItem(itemId);
            progressBar.update();
        };

        params.beforeSendRequest = function (sendRequest) {
            const beginOffset = event.target.cumulativeOffset();
            itemId = getComponent('asyncProgressBarWrapper').fly(beginOffset,
                params.taskName,
                function () {
                    sendRequest();
                });
        };

        this.execGroupOperation(params);
    },

    execGroupOperation(params) {
        const context = this;

        const ids = $H();
        let count = 0;

        const idsParamName = params.submitVarName
            ? params.submitVarName
            : 'ids';

        const selectedItems = params.selectedItems ? params.selectedItems : this.getSelectedItemsIds();
        selectedItems.each(function (id) {
            ids.set(`${idsParamName}[${count}]`, id);
            count++;
        });

        if (params.checkSelection) {
            if (!params.checkSelection()) {
                return;
            }
        } else if (!count) {
            this._showItemsNotSelectedWarning();
            return;
        } else {
            this._hideItemsNotSelectedWarning();
        }

        const submit = this._submit;
        const sendRequestHandler = params.submitHandler
            ? params.submitHandler
            : function (url, ids) {
                const parameters = {
                    method: 'post',
                    parameters: ids,
                    reloading: false,
                    context,

                    onSuccess(transport) {
                        let status;
                        try {
                            status = transport.responseText.evalJSON();
                        } catch (e) {
                            showInternalError(transport.responseText);
                            return;
                        }
                        if (status.redirect) {
                            redirect(status.redirect);
                        } else if (!status.noReload) {
                            parameters.reloading = true;
                            context.reload();
                        }

                        if (context._clearStatusOnReload) {
                            clearStatusMessages();
                        }
                        $A(status.statusMessages).each(function (message) {
                            addStatusMessage(message.status, message.content);
                        });

                        if (params.onSuccess) {
                            params.onSuccess(status);
                        }
                    },

                    onCreate() {
                        context.disable();
                    },

                    onComplete() {
                        if (!parameters.reloading) {
                            context.enable();
                        }
                    },

                    onFailure(req) {
                        if (504 === req.status) {
                            addStatusMessage('error', context.lmsg('timeoutMessage'));
                        } else {
                            showInternalError(req.responseText);
                        }

                        context.enable();
                    },
                };

                submit(prepareUrl(url), parameters);
            };

        const sendRequest = function () {
            if (params.beforeSendRequest) {
                params.beforeSendRequest(function () {
                    sendRequestHandler(params.url, ids);
                });
            } else {
                sendRequestHandler(params.url, ids);
            }
        };

        let { skipConfirmation } = params;
        const { mouseEvent } = params;

        if (mouseEvent && !skipConfirmation) {
            mouseEvent.preventDefault();
            skipConfirmation = mouseEvent.shiftKey;
        }

        if (skipConfirmation) {
            sendRequest();
        } else {
            params.confirmationPopup = params.confirmationPopup || this._confirmationPopup.bind(this, params, sendRequest);
            params.confirmationPopup(ids);
        }
    },

    _submit(url, params) {
        new Ajax.Request(url, params);
    },

    _confirmationPopup(params, sendRequest, ids) {
        messageBox.show({
            type: messageBox.TYPE_YESNO,
            buttonTitles: {
                yes: this.lmsg('messageBoxButtonYes'),
                no: this.lmsg('messageBoxButtonNo'),
                wait: this.lmsg('messageBoxButtonWait'),
            },
            text: params.getConfirmOnGroupOperation
                ? params.getConfirmOnGroupOperation()
                : params.locale.confirmOnGroupOperation,
            subtype: params.subtype,
            needAttention: params.needAttention ? params.needAttention : false,
            needAttentionText: params.needAttentionText ? params.needAttentionText : '',
            needAttentionBlockSubmit: params.needAttentionBlockSubmit ? params.needAttentionBlockSubmit : false,
            onYesClick: sendRequest,

            isAjax: params.isAjax,
            requestUrl: params.requestUrl,
            requestParams: ids,
            loadingTitle: params.loadingTitle,
        });
    },

    _showItemsNotSelectedWarning() {
        this._hideItemsNotSelectedWarning();
        const element = this._componentElement.down('items-not-selected-warning');
        if (element) {
            element.show();
        } else {
            this._componentElement.down('.actions-box').insert({
                top: '<div class="actions-msg-container" id="items-not-selected-warning">' +
                     `<span class="list-actions-msg"><span>${this.lmsg('itemsNotSelected')}</span></span>` +
                     '</div>',
            });
        }
    },

    _hideItemsNotSelectedWarning() {
        const element = this._componentElement.down('.actions-msg-container');
        if (element) {
            element.hide();
        }
    },

    render($super) {
        $super();

        if (this._getColumns().indexOf(list.COLUMN_ACTIONS) !== -1) {
            this._contextMenu = new ListContextMenu({
                renderTo: document.body,
                list: this,
            });
        }

        if (!this._data) {
            this.reload();
            return;
        }

        this.redraw();
    },

    _addEvents() {
        $(document).observe('click', function () {
            const paging = this._componentElement.select('.paging').first();
            if (paging) {
                paging.removeClassName('r-paging-visible');
            }
        }.bind(this));
    },

    reload(urlParams, place) {
        urlParams = urlParams || '';

        if (!this._dataUrl) {
            return;
        }

        let reloadUrl = addUrlParams(this._dataUrl, urlParams);

        if (this._additionalData && this._additionalData.controllerName) {
            reloadUrl = addUrlParams(reloadUrl, { controllerName: this._additionalData.controllerName });
        }
        if (this._additionalData && this._additionalData.actionName) {
            reloadUrl = addUrlParams(reloadUrl, { actionName: this._additionalData.actionName });
        }

        if (this._request) {
            this._request.abort();
        }

        this._request = new Ajax.Request(prepareUrl(reloadUrl), {
            method: 'get',
            onSuccess: this._onReloadSuccess.bind(this, place),
            onFailure: this._onReloadFailure.bind(this),
            onException: this._onReloadException.bind(this),
            onCreate: this._onReloadCreate.bind(this),
            onComplete: this._onReloadComplete.bind(this),
        });
        return this._request;
    },

    _onReloadSuccess(place, transport) {
        if ('' == transport.responseText) {
            // :INFO: sometimes happens in FF if you are go to other page during request
            return;
        }

        let data = {};
        try {
            data = transport.responseText.evalJSON();
        } catch (e) {
            showInternalError(transport.responseText);
            return;
        }
        if (data.redirect) {
            redirect(data.redirect);
        }
        const dataLen = this._splitListData(data, place);
        if (false === dataLen) {
            this.processReloadError(data);
        } else {
            this.redraw(place, dataLen);
        }
    },

    _onReloadFailure(transport) {
        showInternalError(transport.responseText);
    },

    _onReloadException(transport, exception) {
        showInternalError(`${exception}\n${transport.responseText}`);
    },

    _onReloadCreate(transport) {
        if (this._clearStatusOnReload) {
            clearStatusMessages();
        }
        this.disable();
    },

    _onReloadComplete() {
        this._request = null;
        this.enable();
    },

    _addDisabledCheckboxesHints() {
        const checkboxes = this._componentElement.select('input[type="checkbox"][disabled]');

        checkboxes.each(function (checkbox) {
            new Tooltip.Instance(checkbox.previous(), { text: this._disabledSelectHint });
        }, this);
    },

    /**
     * @param {String} [place]
     * @param {Number} [dataLen]
     */
    redrawPartial(place, dataLen) {
        if (!dataLen) {
            return;
        }

        if ('before' === place) {
            $(this._tableId).down('thead').insert({
                after: this._getListDataHtml(this._data.slice(0, dataLen)),
            });
        } else if ('after' === place) {
            $(this._tableId).insert({
                bottom: this._getListDataHtml(this._data.slice(-dataLen)),
            });
        }

        Tooltip.initData(this._componentElement);
        this.updateFilter(true);
    },

    /**
     * @param {String} [place]
     * @param {Number} [dataLen]
     */
    redraw(place, dataLen) {
        if (place && $(this._tableId)) {
            this.redrawPartial(place, dataLen);
            this._onRedraw();
            return;
        }

        this._componentElement.down().remove();
        render(this._componentElement, this.containerView(), 'top');

        if (this._operations || this._searchFilters.size()) {
            if (this._state.forceShowSearch) {
                Cookie.set(`${this._id}-search-show`, 'true');
            }

            this._actionBox = new SmallTools({
                locale: this.getLocale(),
                operationsId: `${this._id}-operations`,
                renderTo: this._componentElement.down(),
                renderMode: 'top',
                operations: this._getOperations(),
                listId: this._id,
                searchFilters: this._searchFilters,
                searchFiltersState: this._state.searchFilters,
                searchOveral: this._searchOveral,
                searchHandler: this._onSearchClick.bind(this),
                resetSearchHandler: this._onResetSearchClick.bind(this),
                searchCollapsed: 'true' !== Cookie.get(`${this._id}-search-show`),
                onToggleSearch: function (collapsed) {
                    Cookie.set(`${this._id}-search-show`, !collapsed);
                    this._hideItemsNotSelectedWarning();
                }.bind(this),
                quickSearchInputId: `${this._id}-search-text-${this._getSearchOveralField()}`,
            });
        }

        if (this._isColumnSelectionPresent() && this._pager.totalItemCount > 0) {
            this._addSelectionHandlers();
        }
        if (this._contextMenu) {
            this._contextMenu.onRedraw();
        }

        this._addPathbar();

        if (this._disabledSelectHint) {
            this._addDisabledCheckboxesHints();
        }

        this._addResponsiveHtml();

        this._initDropdowns();
        this._initItemActions();
        Tooltip.initData(this._componentElement);
        this.updateFilter(true);
        this._onRedraw();
    },

    _initItemActions() {
        this._componentElement.select('a[data-action-name]').invoke('observe', 'click', function (event) {
            event.preventDefault();
            const link = event.findElement('a[data-action-name]');
            const actionName = link.getAttribute('data-action-name');
            if (!this._itemActions[actionName]) {
                return;
            }

            const itemId = link.up('tr').getAttribute('data-row-id');
            const row = this._data.find(function (item) {
                return item.id === itemId;
            });
            this._itemActions[actionName](row, event);
        }.bind(this));
    },

    _initDropdowns() {
        let dropdown;
        this._dropdowns = this._dropdowns || [];
        while ((dropdown = this._dropdowns.pop())) {
            DropdownManager.unregister(dropdown);
        }

        this._componentElement.select('.dropdown .input-group').each(function (el) {
            this._dropdowns.push(DropdownManager.register(el,
                function () {
                    return el.up().hasClassName('open');
                },
                function () {
                    el.up().addClassName('open');
                },
                function () {
                    el.up().removeClassName('open');
                }
            ));
        }, this);
    },

    /**
     * @param {*} data
     */
    processReloadError(data) {
        if (data && data.statusMessages) {
            $A(data.statusMessages).each(function (message) {
                addStatusMessage(message.status, message.content);
            });
        } else {
            showInternalError('Unable to load list data.');
        }
    },

    /**
     * @param {Function} callback
     * @returns {Boolean}
     */
    hasSelectedItems(callback) {
        const selected = this.getSelectedItemsIds();

        if (!selected.length) {
            this._showItemsNotSelectedWarning();
            return false;
        }
        this._hideItemsNotSelectedWarning();

        if (callback) {
            callback.bind(this)();
        }

        return true;
    },

    getItemById(itemId) {
        for (let i = 0; i < this._data.length; i++) {
            if (this._data[i].id == itemId) {
                return this._data[i];
            }
        }
    },

    _addResponsiveHtml() {
        const list = this;
        const columns = this._componentElement.select('thead th');
        this._componentElement.select(`#${this._tableId} tbody tr`).each(function (row) {
            const cells = row.childElements();
            for (let i = 0; i < cells.length; i++) {
                const element = cells[i];
                const columnName = columns[i].innerHTML.strip().stripTags();
                const columnNameHtml = `<span class="r-visible">${columnName}</span>`;

                list._addResponsiveHtmlToButtons(element, columnNameHtml);

                list._addResponsiveHtmlToImages(element, columnNameHtml);

                list._addResponsiveHtmlToIcons(element, columnNameHtml);

                list._addResponsiveHtmlToNumbers(element, columnName);
            }
        });
    },

    _addResponsiveHtmlToButtons(element, columnNameHtml) {
        const buttons = element.select('a.s-btn');
        buttons.each(function (button) {
            const tooltip = button.previous('.tooltipData');
            if (!button.down('span').innerHTML && tooltip) {
                button.down('span').innerHTML = `<span class="r-visible">${tooltip.innerHTML}</span>`;
            }
        });
        if (buttons.length === 1 && !buttons[0].down('span').innerHTML) {
            buttons[0].down('span').innerHTML = columnNameHtml;
        }
    },

    _addResponsiveHtmlToImages(element, columnNameHtml) {
        const images = element.select('a img,a [class^="icon-"],a [class*=" icon-"]');
        images.each(function (image) {
            let text = '';
            const tooltip = image.up().previous('.tooltipData');
            if (!tooltip) {
                if (images.length !== 1) {
                    return;
                }
                text = columnNameHtml;
            } else {
                text = tooltip.innerHTML;
            }
            if (!image.up('.b-indent-icon') || !image.up('a').innerHTML.replace(image.up('.b-indent-icon').outerHTML, '').strip()) {
                let wrapper = image;
                if ('img' === image.tagName.toLowerCase()) {
                    wrapper = image.wrap('i', { class: 'icon' });
                }
                wrapper.insert({ after: ` <span class="r-visible">${text}</span>` });
                image.up('a').addClassName('i-link');
            }
        });
    },

    _addResponsiveHtmlToIcons(element, columnNameHtml) {
        const icons = element.select('span.b-indent-icon');
        icons.each(function (icon) {
            const tooltip = icon.select('.tooltipData');
            if (tooltip.length > 0 && !icon.up().innerHTML.replace(icon.outerHTML, '').strip()) {
                icon.down('img').insert({ after: ` <span class="r-visible">${tooltip[0].innerHTML}</span>` });
            }
        });
        if (icons.length === 1 && !icons[0].down('span.r-visible') && !icons[0].up().innerHTML.replace(icons[0].outerHTML, '').strip()) {
            icons[0].down('img').insert({ after: columnNameHtml });
        }
    },

    _addResponsiveHtmlToNumbers(element, columnName) {
        const number = element.innerHTML;
        const columnNameHtml = `<span class="r-visible">${columnName}: </span>`;
        if ('-' === number || (!isNaN(parseFloat(number)) && isFinite(number))) {
            element.insert({ top: columnNameHtml });
        }
        const links = element.select('a');
        if (
            links.length === 1 &&
            ('-' === links[0].innerHTML || (!isNaN(parseFloat(links[0].innerHTML)) && isFinite(links[0].innerHTML)))
        ) {
            element.insert({ top: columnNameHtml });
        }
    },

    checkSyncStatus(statusElementId, url, onFinishCallback, randomId, options) {
        // list was reloaded - stop the check of sync status for old list elements
        if (this._randomId !== randomId) {
            return;
        }

        const context = this;
        options = $H({
            timeout: 5000,
            progressStatus: 'started',
        }).merge($H(options));
        new Ajax.Request(url, {
            method: 'get',
            parameters: {},
            onSuccess(transport) {
                const result = transport.responseText.evalJSON();
                const statusElement = $(statusElementId);
                if (!statusElement) {
                    return;
                }
                if (options.get('progressStatus') === result.status) {
                    setTimeout(function () {
                        context.checkSyncStatus(statusElementId, url, onFinishCallback, randomId, options);
                    }, options.get('timeout'));
                } else {
                    onFinishCallback(statusElement, result);
                }
            },
        });
    },

    _getOperations() {
        const operations = this._operations ? this._operations.slice() : [];
        if (!this._additionalData || !this._additionalData.operations || !this._additionalData.operations.length) {
            return operations;
        }
        const deleteButtonIndex = this._getDeleteButtonIndex();
        let args = [deleteButtonIndex, 0];
        if (deleteButtonIndex !== 0 && operations[deleteButtonIndex - 1].componentType !== 'Jsw.bar.Separator') {
            args.push({ componentType: 'Jsw.bar.Separator' });
        }
        args = args.concat(this._additionalData.operations);
        if (deleteButtonIndex !== operations.length) {
            args.push({ componentType: 'Jsw.bar.Separator' });
        }
        operations.splice(...args);
        return operations;
    },

    _getColumns() {
        const columns = this._columns.slice();

        const extensions = (this._additionalData && this._additionalData.extensions) || {};
        Object.keys(extensions).forEach(function (id) {
            this._modifyColumnsByExtension(columns, extensions[id]);
        }.bind(this));

        return columns;
    },

    _modifyColumnsByExtension(columns, extension) {
        const findColumn = dataIndex => {
            const column = columns.find(function (column) {
                return column.dataIndex === dataIndex;
            });

            dataIndex = parseInt(dataIndex);
            return column || columns[dataIndex < 0 ? dataIndex + columns.length : dataIndex - 1];
        };

        if (extension.columnsOverride) {
            Object.keys(extension.columnsOverride).forEach(function (dataIndex) {
                const originalColumn = findColumn(dataIndex);
                if (!originalColumn) {
                    return;
                }

                const column = extension.columnsOverride[dataIndex];

                if (column.title) {
                    column.header = column.title;
                }

                if (column.renderer) {
                    // eslint-disable-next-line no-eval
                    column.renderer = eval(`(${column.renderer})`);
                }

                if (!Object.isUndefined(column.isVisible) && !column.isVisible) {
                    column.isVisible = function () {
                        return false;
                    };
                }

                Object.extend(originalColumn, column);
            });
        }

        if (extension.columns) {
            Object.keys(extension.columns).forEach(function (dataIndex) {
                let column = extension.columns[dataIndex];

                if (column.renderer) {
                    // eslint-disable-next-line no-eval
                    column.renderer = eval(`(${column.renderer})`);
                }

                column = Object.extend({
                    header: column.title,
                    dataIndex,
                }, column);

                let index;
                let originalColumn;

                if (column.insertBefore && (originalColumn = findColumn(column.insertBefore))) {
                    index = columns.indexOf(originalColumn);
                } else if (column.insertAfter && (originalColumn = findColumn(column.insertAfter))) {
                    index = columns.indexOf(originalColumn) + 1;
                } else {
                    index = columns.length;
                }

                columns.splice(index, 0, column);
            });
        }
    },

    _getDeleteButtonIndex() {
        if (!this._operations) {
            return 0;
        }
        let deleteButtonIndex = this._operations.length;
        this._operations.forEach(function (el, i) {
            if (el.addCls === 'sb-remove-selected') {
                deleteButtonIndex = i;
            }
        });
        return deleteButtonIndex;
    },
});



// WEBPACK FOOTER //
// ./jsw/list.js